import { caseEntryTypes } from '../types/caseEntry'
import type { CaseEntry, CaseEntryType, CaseEntryUnit } from '../types/caseEntry'
import type { InvoiceLine } from '../types/invoice'

export function entrySubtotal(entry: CaseEntry): number {
  const quantity = Number.isFinite(entry.quantity) ? entry.quantity : 0
  const price = Number.isFinite(entry.unitPrice) ? entry.unitPrice : 0
  return quantity * price
}

export function entryTotal(entry: CaseEntry): number {
  return entrySubtotal(entry) * (1 + (entry.vatRate || 0) / 100)
}

export interface CaseEntryTotals {
  hours: number
  materials: number
  travelService: number
  other: number
  subtotal: number
  vatAmount: number
  total: number
  /** Samlet antal registrerede timer. */
  hoursCount: number
}

/** Beløb er ekskl. moms pr. gruppe; moms og total samlet nederst. */
export function caseEntryTotals(entries: CaseEntry[]): CaseEntryTotals {
  const sumOf = (types: CaseEntryType[]) =>
    entries.filter((entry) => types.includes(entry.type)).reduce((sum, entry) => sum + entrySubtotal(entry), 0)

  const subtotal = entries.reduce((sum, entry) => sum + entrySubtotal(entry), 0)
  const vatAmount = entries.reduce(
    (sum, entry) => sum + entrySubtotal(entry) * ((entry.vatRate || 0) / 100),
    0,
  )
  return {
    hours: sumOf(['Timer']),
    materials: sumOf(['Materialer']),
    travelService: sumOf(['Kørsel', 'Servicevogn og værktøj']),
    other: sumOf(['Maskiner', 'Bortskaffelse', 'Andet']),
    subtotal,
    vatAmount,
    total: subtotal + vatAmount,
    hoursCount: entries
      .filter((entry) => entry.type === 'Timer')
      .reduce((sum, entry) => sum + (entry.quantity || 0), 0),
  }
}

/** Standardenhed for hver type, så formularen er hurtig at udfylde. */
export const defaultUnitFor: Record<CaseEntryType, CaseEntryUnit> = {
  Timer: 'timer',
  Materialer: 'stk.',
  Kørsel: 'km',
  'Servicevogn og værktøj': 'fast pris',
  Maskiner: 'stk.',
  Bortskaffelse: 'stk.',
  Andet: 'stk.',
}

export function sortEntries(entries: CaseEntry[]): CaseEntry[] {
  return [...entries].sort((a, b) => {
    const byType = caseEntryTypes.indexOf(a.type) - caseEntryTypes.indexOf(b.type)
    if (byType !== 0) return byType
    return (a.date || a.createdAt).localeCompare(b.date || b.createdAt)
  })
}

const shortDate = new Intl.DateTimeFormat('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' })

export function formatEntryDate(iso: string): string {
  if (!iso) return ''
  const date = new Date(`${iso}T12:00:00`)
  return Number.isNaN(date.getTime()) ? iso : shortDate.format(date)
}

/**
 * Forbruget kopieres til fakturalinjer. Fakturaen forbliver fuldt redigerbar —
 * den låses ikke til sagens data.
 */
export function invoiceLinesFromEntries(entries: CaseEntry[]): InvoiceLine[] {
  return sortEntries(entries).map((entry) => {
    const base = entry.description || entry.type
    const description =
      entry.type === 'Timer' && entry.date ? `${base} – ${formatEntryDate(entry.date)}` : base
    return {
      id: `iline-${Math.random().toString(36).slice(2, 9)}`,
      description,
      quantity: entry.quantity,
      unit: entry.unit,
      unitPrice: entry.unitPrice,
      vatRate: entry.vatRate,
      discount: 0,
    }
  })
}
